import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Modal } from '../../components/atoms/Modal'
import { Textarea } from '../../components/atoms/Textarea'
import { ORDERSTATUS } from '../../../constants/order-status.constants'
import type { OrderReturnListDto } from '../../../viewmodels/useOrderManagementReturns'

const NOTES_MAX = 1000

interface Props {
  readonly open: boolean
  readonly row: OrderReturnListDto
  readonly decision: 'accept' | 'reject'
  readonly onClose: () => void
  readonly onConfirm: (
    nextStatus: string,
    notes: string | undefined
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

/**
 * Forces the operator to read the customer's return reason before
 * accepting or rejecting — opens with the comment front-and-centre,
 * an optional internal-notes field, and a single Confirm button that
 * commits the transition. Kiran 2026-06-18.
 */
export function ReturnDecisionModal({ open, row, decision, onClose, onConfirm }: Props): JSX.Element {
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const customerName = (() => {
    const n = [row.customer_first_name, row.customer_last_name].filter(Boolean).join(' ')
    return n.length > 0 ? n : row.customer_email
  })()

  const isAccept = decision === 'accept'
  const targetStatus = isAccept ? ORDERSTATUS.RETURNCONFIRMED : ORDERSTATUS.REJECTED

  const handleConfirm = async (): Promise<void> => {
    setError(null)
    if (notes.length > NOTES_MAX) {
      setError(`Notes exceed ${NOTES_MAX} characters`)
      return
    }
    setSubmitting(true)
    const trimmed = notes.trim()
    const res = await onConfirm(targetStatus, trimmed.length > 0 ? trimmed : undefined)
    setSubmitting(false)
    if (!res.ok) {
      setError(res.message)
    } else {
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isAccept ? 'Accept return request' : 'Reject return request'}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={submitting}>
            {submitting ? 'Saving…' : isAccept ? 'Confirm accept' : 'Confirm reject'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        <section className="rounded-lg border border-border bg-parchment/40 p-4 space-y-2">
          <p className="font-body text-body-xs uppercase tracking-wide text-text-secondary">
            From {customerName}
          </p>
          <p className="font-body text-body-xs text-text-secondary">
            Order <span className="font-mono">UYM-{row.order_id.slice(0, 8).toUpperCase()}</span>
            {' · '}requested {formatDate(row.requested_at)}
            {row.organisation_name !== null ? ` · ${row.organisation_name}` : ''}
          </p>
          <hr className="border-border" />
          <p className="font-body text-body text-charcoal whitespace-pre-wrap">
            {row.reason}
          </p>
        </section>

        <section className="space-y-2">
          <label className="font-body text-body-sm text-charcoal">
            Internal note (optional)
          </label>
          <Textarea
            value={notes}
            rows={3}
            maxLength={NOTES_MAX}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              isAccept
                ? 'Notes to attach to this acceptance — visible on the order timeline.'
                : 'Reason for the rejection — visible on the order timeline.'
            }
          />
          <p className="font-body text-body-xs text-text-secondary text-right">
            {notes.length} / {NOTES_MAX}
          </p>
        </section>
      </div>
    </Modal>
  )
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}
